import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, DragOverlay, PointerSensor, closestCorners, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { postResource, deleteTask } from "../../../apis/apiService";
import { toast } from "react-toastify";
import "./SubtaskBoard.css";
import { Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { FiPlus, FiX, FiChevronDown, FiChevronUp, FiCalendar, FiEdit2, FiTrash2, FiCheck } from "react-icons/fi";
import { TbArrowUp, TbArrowRight, TbArrowDown } from "react-icons/tb";
import Loader from "../../common/Loader";



// ── helpers ─────────────────────────────────────────────────────────────────
const getInitials = (n = "") => n.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("") || "?";
const PAL = ["#0052CC", "#00875A", "#FF5630", "#FF8B00", "#6554C0", "#00B8D9", "#36B37E", "#DE350B"];
const aColor = (n = "") => { let h = 0; for (const c of n) h = c.charCodeAt(0) + ((h << 5) - h); return PAL[Math.abs(h) % PAL.length]; };
const fmtDate = d => { if (!d) return null; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const isOverdue = d => d && new Date(d) < new Date();
const SWATCHES = ["#0052CC", "#00875A", "#FF5630", "#FF8B00", "#6554C0", "#00B8D9", "#36B37E", "#DE350B", "#403294", "#172B4D"];

const PriorityIcon = ({ name = "", colour = "" }) => {
  const n = name.toUpperCase();
  const Icon = n === "HIGH" ? TbArrowUp : n === "LOW" ? TbArrowDown : TbArrowRight;
  const c = colour || (n === "HIGH" ? "#FF5630" : n === "LOW" ? "#36B37E" : "#FF8B00");
  return <Tooltip title={name || "Medium"} placement="top" ><span style={{ color: c, display: "inline-flex", alignItems: "center" }}><Icon size={13} /></span></Tooltip>;
};

// ── Confirm Modal ───────────────────────────────────────────────────────────
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  return (
    <Dialog open={isOpen} onClose={onCancel}>
      <DialogTitle>{title || "Confirm Deletion"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message || "Are you sure?"}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>NO</Button>
        <Button onClick={onConfirm} color="error" sx={{ px: 3 }}>YES</Button>
      </DialogActions>
    </Dialog>
  );
};


// ── Kanban Card ──────────────────────────────────────────────────────────────
const KanbanCard = ({ task, colColor, onOpen, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const due = task.end_date || task.dueDate;
  const late = isOverdue(due);
  const owners = useMemo(() => {
    if (Array.isArray(task.owners) && task.owners.length > 0) return task.owners;
    if (Array.isArray(task.owner) && task.owner.length > 0) return task.owner;
    if (task.owner_name) {
      // Fallback: If owners array is missing but we have a (possibly comma-separated) owner_name string
      return String(task.owner_name).split(",").map(name => ({ name: name.trim() }));
    }
    return [];
  }, [task.owners, task.owner, task.owner_name]);

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: task.id });

  useEffect(() => {
    if (!menu) return;
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menu]);

  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  // Flow: Admin/Manager have full access. Employees can only drag if they are owner or creator.
  const canDrag = (
    ["ADMIN", "MANAGER"].includes(login_user?.role) ||
    task?.added_by === login_user?.id ||
    owners?.some((owner) => owner.user_id === login_user?.id)
  );

  const cardInner = (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
      }}
      className={`jk-card ${!canDrag ? "jk-card--locked" : ""}`}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
    >
      <div className="jk-card-stripe" style={{ background: colColor }} />
      <div className="jk-card-inner">
        <div className="jk-card-head">
          <Tooltip title="View & Edit Task" placement="top">
            <span
              className="jk-card-title"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(task);
              }}
            >
              {task.title || "Untitled"}
            </span>
          </Tooltip>
          <div className="jk-card-dates-box">
            <Tooltip
              title={
                <div>
                  <div>Start Date: {fmtDate(task.start_date || task.startDate) || "No date"}</div>
                  <div>End Date: {fmtDate(task.end_date || task.dueDate) || "No date"}</div>
                </div>
              }
              placement="top"
            >
              <div className={`jk-card-date-icon-wrap${late ? " late" : ""}`}>
                <FiCalendar size={14} />
              </div>
            </Tooltip>
          </div>
        </div>

        <div className="jk-card-mid">
          <span className="jk-card-key">#{task.id}</span>
          <div className="jk-av-stack">
            {owners.length > 0 ? (
              owners.map((o, idx) => {
                const isUnassigned = !o.name || o.name.toLowerCase() === "unassigned";
                if (isUnassigned) {
                  return (
                    <div
                      key={idx}
                      style={{
                        fontSize: "10px",
                        color: "#555",
                        backgroundColor: "#f0f0f0",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        border: "1px solid #ddd",
                        display: "inline-flex",
                        alignItems: "center",
                        fontWeight: "500",
                      }}
                    >
                      Unassigned
                    </div>
                  );
                }
                return (
                  <Tooltip key={idx} title={o.name} placement="top">
                    <div className="jk-av" style={{ background: aColor(o.name) }}>
                      {getInitials(o.name)}
                    </div>
                  </Tooltip>
                );
              })
            ) : (
              <div
                style={{
                  fontSize: "10px",
                  color: "#666",
                  backgroundColor: "#f8f9fa",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  border: "1px dashed #ccc",
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: "500",
                }}
              >
                Unassigned
              </div>
            )}
          </div>
        </div>

        <div className="jk-card-bottom">
          {/* {(task.priority_name || task.priority) && (
            <span className="jk-card-prio-text" style={{ color: task.priority_colour }}>
              {task.priority_name || task.priority}
            </span>
          )} */}
          
          {(task.priority_name || task.priority) && (
            <span
              className="jk-card-prio-text"
              style={{ color: task.priority_colour }}
            >
              {task.priority_name || task.priority}
            </span>
          )}

          {/* Added Status Name to card */}
          {(task.current_status_name || task.status_name) && (
            <span
              className="jk-card-status-text"
              style={{
                color: task.status_colour || "#888",
                marginLeft: "10px",
                fontSize: "10px",
                fontWeight: "bold",
              }}
            >
              {task.current_status_name || task.status_name}
            </span>
          )}

          <div className="jk-card-fr">
            {(task.priority_name || task.priority) && (
              <PriorityIcon
                name={task.priority_name || task.priority}
                colour={task.priority_colour}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return !canDrag ? (
    <Tooltip
      title="You don't have permission to move this task"
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -65],
              },
            },
          ],
        },
      }}
    >
      {cardInner}
    </Tooltip>
  ) : (
    cardInner
  );

};

const GhostCard = ({ task }) => (
  <div className="jk-card jk-card-ghost">
    <div className="jk-card-stripe" style={{ background: "#0052CC" }} />
    <div className="jk-card-inner"><span className="jk-card-title">{task?.title || "Task"}</span></div>
  </div>
);

// ── Column Header Menu (edit title, change color, delete) ───────────────────
const ColHeaderMenu = ({ col, onRename, onRecolor, onDelete, onClose }) => {
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(col.name);
  const [colorMode, setColorMode] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const commitRename = () => { if (newName.trim()) { onRename(newName.trim()); onClose(); } };

  return (
    <div className="jk-col-menu" onClick={e => e.stopPropagation()}>
      {!editing && !colorMode && <>
        <button className="jk-cm-item" onClick={() => setEditing(true)}><FiEdit2 size={12} /> Rename</button>
        <button className="jk-cm-item" onClick={() => setColorMode(true)}><span className="jk-cm-dot" style={{ background: col.color }} /> Change color</button>
        <div className="jk-cm-divider" />
        <button className="jk-cm-item jk-cm-danger" onClick={() => { onDelete(); onClose(); }}><FiTrash2 size={12} /> Delete column</button>
      </>}

      {editing && <>
        <input ref={ref} className="jk-cm-input" value={newName} onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setEditing(false); setNewName(col.name); } }} />
        <div className="jk-qf-row" style={{ padding: "4px 0 0" }}>
          <button className="jk-btn-p" style={{ fontSize: 11 }} onClick={commitRename}><FiCheck size={12} /> Save</button>
          <button className="jk-btn-g" onClick={() => { setEditing(false); setNewName(col.name); }}><FiX size={12} /></button>
        </div>
      </>}

      {colorMode && <>
        <p style={{ margin: "0 0 6px", fontSize: 11, color: "#888" }}>Pick color</p>
        <div className="jk-acf-swatches">
          {SWATCHES.map(c => (
            <button key={c} className={`jk-swatch${col.color === c ? " sel" : ""}`} style={{ background: c }}
              onClick={() => { onRecolor(c); onClose(); }} />
          ))}
        </div>
        <button className="jk-btn-g" style={{ marginTop: 6, width: "100%" }} onClick={() => setColorMode(false)}>← Back</button>
      </>}
    </div>
  );
};

// ── Column ───────────────────────────────────────────────────────────────────
const Column = ({ col, tasks, numericId, onOpen, onDelete, onRefresh, collapsedMap, onToggle, onUpdateCol, onDeleteCol, isClient }) => {
  // const [create, setCreate] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const collapsed = collapsedMap[col.id];
  const ids = tasks.map(t => t.id);
  const menuRef = useRef(null);

  const { setNodeRef } = useDroppable({ id: col.id });

  // close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  return (
    <div className={`jk-col${collapsed ? " jk-col--collapsed" : ""}`}>
      {/* Header */}
      <div className="jk-col-header">
        {!collapsed && <>
          <span className="jk-col-name">{col.name.toUpperCase()}</span>
          <span className="jk-col-count">{tasks.length}</span>
        </>}
        <div className="jk-col-actions">
          {/* {!collapsed && <button className="jk-col-btn" onClick={() => setCreate(p => !p)}><FiPlus size={14} /></button>} */}
          {/* Three-dot menu button */}
          {/* <div style={{ position: "relative" }} ref={menuRef}>
            <button className="jk-col-btn" onClick={() => setShowMenu(p => !p)}><FiMoreHorizontal size={14} /></button>
            {showMenu && (
              <ColHeaderMenu
                col={col}
                onRename={name => onUpdateCol(col.id, { name })}
                onRecolor={color => onUpdateCol(col.id, { color })}
                onDelete={() => onDeleteCol(col.id)}
                onClose={() => setShowMenu(false)}
              />
            )}
          </div> */}
          <button className="jk-col-btn" onClick={() => onToggle(col.id)}>
            {collapsed ? <FiChevronDown size={13} /> : <FiChevronUp size={13} />}
          </button>
        </div>
      </div>

      <div className="jk-col-bar" style={{ background: col.color }} />

      {!collapsed && (
        <div ref={setNodeRef} className="jk-cards-zone">
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {tasks.map(t => <KanbanCard key={t.id} task={t} colColor={col.color} onOpen={onOpen} onDelete={onDelete} />)}
          </SortableContext>
          {tasks.length === 0 && !isClient && (
            <div className="jk-drop-zone" style={{ cursor: "default" }}>Drop tasks here</div>
          )}
        </div>
      )}

      {collapsed && (
        <div className="jk-col-vertical">
          <div className="jk-col-vdot" style={{ background: col.color }} />
          <span className="jk-col-vname">{col.name}</span>
          <span className="jk-col-vcount">{tasks.length}</span>
        </div>
      )}
    </div>
  );
};

// ── Add Column ───────────────────────────────────────────────────────────────
const AddColumnButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#0052CC");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open) ref.current?.focus(); }, [open]);

  const formRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (formRef.current && !formRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await onAdd({ name: name.trim(), color }); setOpen(false); setName(""); }
    catch { } finally { setBusy(false); }
  };

  return (
    <div style={{ position: "relative" }}>
      <button className="jk-add-col-btn" onClick={() => setOpen(true)}>
        <FiPlus size={16} /> New Board
      </button>
      {open && (
        <div className="jk-add-col-form" ref={formRef}>
          <p className="jk-acf-label">New column</p>
          <input ref={ref} className="jk-qf-inp" placeholder="Status name…" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setOpen(false); setName(""); } }} disabled={busy} />
          <div className="jk-acf-swatches">
            {SWATCHES.map(c => <button key={c} className={`jk-swatch${color === c ? " sel" : ""}`} style={{ background: c }} onClick={() => setColor(c)} />)}
          </div>
          <div className="jk-acf-bar" style={{ background: color }} />
          <div className="jk-qf-row"><button className="jk-btn-p" onClick={save} disabled={!name.trim() || busy}>{busy ? "Adding…" : "Add"}</button><button className="jk-btn-g" onClick={() => { setOpen(false); setName("") }}><FiX size={14} /></button></div>
        </div>
      )}
    </div>
  );
};

const SubtaskBoard = ({ numericId, allTasks, onRefresh, loading, onOpenTask, searchTerm = "" }) => {
  const { id: routeId } = useParams();
  const project_id = localStorage.getItem("project_id");
  const navigate = useNavigate();
  const location = useLocation();

  const StatusOptions = useSelector(s => s.selectOptions?.StatusOptions);
  const projectUsers = useSelector(s => s.selectOptions?.userNamesByProjects) || [];
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const isClient = login_user?.role === "CLIENT";

  // const statusList = useMemo(() => {
  //   const grp = StatusOptions?.find(s => s.status_type === "TASK");
  //   return (grp?.status_details ?? []).map(s => ({ id: s.status_id, name: s.status_name, color: s.status_colour || "#CCCCCC" }));
  // }, [StatusOptions]);

  const statusList = useMemo(() => {
    const grp = StatusOptions?.find(s => s.status_type === "TASK");
    return (grp?.status_details ?? [])
      // .filter(s => s.is_visible !== 0 && s.is_visible !== "0" && s.is_visible !== false)
      .filter(s => s.is_kanban_view === 1 || s.is_kanban_view === "1" || s.is_kanban_view === true)
      .map(s => ({ id: s.status_id, name: s.status_name, color: s.status_colour || "#CCCCCC" }));
  }, [StatusOptions]);

  const [columns, setColumns] = useState([]);
  const [taskMap, setTaskMap] = useState({});
  const [activeTask, setActiveTask] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [confirm, setConfirm] = useState({ open: false, task: null });
  const [search, setSearch] = useState("");

  // Sync external searchTerm (from Task.js top search bar) into internal search state
  useEffect(() => {
    setSearch(searchTerm || "");
  }, [searchTerm]);
  const [showFilt, setShowFilt] = useState(false);
  const [fAss, setFAss] = useState("all");
  const [fPri, setFPri] = useState("all");
  const [updating, setUpdating] = useState(false);
  const startColRef = useRef(null);
  const lastOverColRef = useRef(null);

  // ── Build columns ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (updating) return; // Prevent snapback during API call
    if (!statusList.length) return;
    if (loading && (!allTasks || allTasks.length === 0)) return;

    const map = {};
    allTasks.forEach(t => { map[t.id] = t; });
    setTaskMap(map);

    const cols = statusList.map(s => ({
      ...s,
      taskIds: (allTasks || []).filter(t => Number(t.current_status_id) === Number(s.id)).map(t => t.id),
    }));

    const claimed = new Set(cols.flatMap(c => c.taskIds));
    const leftover = (allTasks || []).filter(t => !claimed.has(t.id)).map(t => t.id);
    if (cols.length && leftover.length) cols[0] = { ...cols[0], taskIds: [...cols[0].taskIds, ...leftover] };

    setColumns(prev => {
      const localOnly = prev.filter(c => String(c.id).startsWith("local_"));
      return [...cols, ...localOnly];
    });
  }, [allTasks, statusList, loading]);

  // ── Column management ─────────────────────────────────────────────────────
  const handleUpdateCol = (id, patch) => {
    setColumns(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const handleDeleteCol = (id) => {
    // Move tasks from deleted column to first remaining column
    setColumns(prev => {
      const col = prev.find(c => c.id === id);
      const rest = prev.filter(c => c.id !== id);
      if (rest.length && col?.taskIds?.length) {
        rest[0] = { ...rest[0], taskIds: [...rest[0].taskIds, ...col.taskIds] };
      }
      return rest;
    });
    toast.success("Column removed");
  };
  const handleAddColumn = ({ name, color }) => {
    setColumns(prev => [...prev, { id: `local_${Date.now()}`, name, color, taskIds: [] }]);
    toast.success(`Column "${name}" added`);
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const visibleTasks = useCallback((colId) => {
    const col = columns.find(c => c.id === colId);
    if (!col) return [];
    return col.taskIds.map(id => taskMap[id]).filter(t => {
      if (!t) return false;
      const searchLower = search ? search.trim().toLowerCase() : "";
      const statusName = t.current_status_name || t.status_name || col.name || "";
      const titleOk = !search ||
        (t.title || t.task_name || t.subtask_name || "").toLowerCase().includes(searchLower) ||
        (t.project_title || t.project_name || "").toLowerCase().includes(searchLower) ||
        (t.task_list_name || t.module_name || "").toLowerCase().includes(searchLower) ||
        String(t.id || t.task_id).includes(searchLower) ||
        statusName.toLowerCase().includes(searchLower);
      const tOwners = Array.isArray(t.owners) && t.owners.length > 0 ? t.owners :
        Array.isArray(t.owner) && t.owner.length > 0 ? t.owner :
          t.owner_name ? String(t.owner_name).split(",").map(n => ({ name: n.trim() })) : [];
      const assOk = fAss === "all" || tOwners.some(o => String(o.user_id || o.name) === String(fAss));
      const priOk = fPri === "all" || (t.priority_name || "").toUpperCase() === fPri;
      return titleOk && assOk && priOk;
    });
  }, [columns, taskMap, search, fAss, fPri]);

  const hasFilter = search || fAss !== "all" || fPri !== "all";
  const totalShown = useMemo(() => columns.reduce((n, c) => n + visibleTasks(c.id).length, 0), [columns, visibleTasks]);

  // ── Drag & Drop ───────────────────────────────────────────────────────────
  // const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const sensors = useSensors(useSensor(PointerSensor, { 
    activationConstraint: isClient ? { distance: 9999 } : { distance: 6 } 
  }));
  // const colOf = id => columns.find(c => c.taskIds.includes(id))?.id ?? null;
  const colOf = id => {
    if (!id) return null;
    const col = columns.find(c => c.taskIds.some(tid => String(tid) === String(id)));
    return col ? col.id : null;
  };

  const onDragStart = ({ active }) => {
    setActiveTask(taskMap[active.id] ?? null);
    startColRef.current = colOf(active.id);
    lastOverColRef.current = colOf(active.id); // init guard
  };

  /* const onDragOver = ({ active, over }) => {
    if (!over) return;
    const from = colOf(active.id);
    const to = columns.find(c => c.id === over.id) ? over.id : colOf(over.id);
    if (!from || !to || from === to) return;
    setColumns(prev => prev.map(c => {
      if (c.id === from) return { ...c, taskIds: c.taskIds.filter(id => id !== active.id) };
      if (c.id === to) { const idx = c.taskIds.indexOf(over.id); const ids = [...c.taskIds]; idx === -1 ? ids.push(active.id) : ids.splice(idx, 0, active.id); return { ...c, taskIds: ids }; }
      return c;
    }));
  }; */

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;

    const from = colOf(activeId);
    // If overId is a column ID, 'to' is that column. If it's a task ID, 'to' is that task's column.
    const to = columns.find(c => String(c.id) === String(overId)) ? overId : colOf(overId);

    if (!from || !to || String(from) === String(to)) return;

    // Guard: skip re-render if already hovering same target column
    if (String(lastOverColRef.current) === String(to)) return;
    lastOverColRef.current = to;

    setColumns(prev => prev.map(c => {
      if (String(c.id) === String(from)) return { ...c, taskIds: c.taskIds.filter(id => String(id) !== String(activeId)) };
      if (String(c.id) === String(to)) {
        const idx = c.taskIds.findIndex(id => String(id) === String(overId));
        const ids = [...c.taskIds];
        idx === -1 ? ids.push(activeId) : ids.splice(idx, 0, activeId);
        return { ...c, taskIds: ids };
      }
      return c;
    }));
  };

  /* const onDragEnd = async ({ active, over }) => {
    setActiveTask(null);
    if (!over) return;
    const from = colOf(active.id);
    const to = columns.find(c => c.id === over.id) ? over.id : colOf(over.id);
    if (!from || !to) return;
    if (from === to) {
      setColumns(prev => prev.map(c => { if (c.id !== from) return c; return { ...c, taskIds: arrayMove(c.taskIds, c.taskIds.indexOf(active.id), c.taskIds.indexOf(over.id)) }; }));
      return;
    }
    if (String(to).startsWith("local_")) {
      setTaskMap(prev => ({ ...prev, [active.id]: { ...prev[active.id], current_status_id: to } }));
      return;
    }
    try {
      await postResource("task", "update", active.id, { current_status_id: to });
      setTaskMap(prev => ({ ...prev, [active.id]: { ...prev[active.id], current_status_id: to } }));
      toast.success("Status updated");
    } catch (e) { toast.error(e?.message || "Update failed"); onRefresh(); }
  }; */

  const onDragEnd = async ({ active, over }) => {
    const activeId = active.id;
    const overId = over?.id;
    const from = startColRef.current;
    setActiveTask(null);
    startColRef.current = null;
    lastOverColRef.current = null;

    if (!overId) return;

    // determine destination column
    const to = columns.find(c => String(c.id) === String(overId)) ? overId : colOf(overId);

    if (!from || !to) return;

    // Same column — local reorder, no API needed
    if (String(from) === String(to)) {
      setColumns(prev => prev.map(c => {
        if (String(c.id) !== String(from)) return c;
        const oldIdx = c.taskIds.findIndex(id => String(id) === String(activeId));
        const newIdx = c.taskIds.findIndex(id => String(id) === String(overId));
        if (oldIdx === -1 || newIdx === -1) return c;
        return { ...c, taskIds: arrayMove(c.taskIds, oldIdx, newIdx) };
      }));
      return;
    }

    // Moving to a different column
    if (String(to).startsWith("local_")) {
      setTaskMap(prev => ({ ...prev, [activeId]: { ...prev[activeId], current_status_id: to } }));
      return;
    }

    // ✅ Optimistic update — card visually stays in new column instantly
    setTaskMap(prev => ({ ...prev, [activeId]: { ...prev[activeId], current_status_id: to } }));

    setUpdating(true);
    try {
      const numericTaskId = activeId.toString().replace(/\D/g, "");
      await postResource("task", "update", numericTaskId, {
        current_status_id: to,
        task_id: numericTaskId
      });
      toast.success("Status updated");
      // ✅ Fire-and-forget — do NOT await, prevents board freeze
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "Update failed");
      // On error: revert by refreshing board
      if (onRefresh) onRefresh();
    } finally {
      setUpdating(false);
    }
  };

  // const openTask = t => navigate(`/projects/projectDetail/${project_id}/taskdetail/${t.id}`);
  const openTask = t => {
    if (onOpenTask) {
      onOpenTask(t);
    } else {
      navigate(`/projects/projectDetail/${project_id}/taskdetail/${t.id}`, { state: { viewMode: "board", from: location.pathname } });
    }
  };

  const handleDeleteTask = (task) => {
    setConfirm({ open: true, task });
  };

  const executeDelete = async () => {
    // const taskId = confirm.task?.id;
    const taskId = confirm.task?.id?.toString().replace(/\D/g, "");
    if (!taskId) return;
    try {
      await deleteTask(taskId);
      toast.success("Task deleted successfully");
      setConfirm({ open: false, task: null });
      onRefresh();
    } catch (e) {
      toast.error(e?.message || "Failed to delete task");
    }
  };

  return (
    <>
      <div className="jk-root" style={{ position: "relative" }}>
        {/* Toolbar */}
        <div className="jk-toolbar">
          <div className="jk-tl">
            {/* <div className="jk-sw">
              <FiSearch className="jk-si" size={13} />
              <input className="jk-search" placeholder="Search board…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button className="jk-sc" onClick={() => setSearch("")}><FiX size={11} /></button>}
            </div>
            <button className="jk-tb-btn" onClick={onRefresh} disabled={loading}>
              <FiRefreshCw size={13} className={loading ? "jk-spin" : ""} />
              </button>
            <AddColumnButton onAdd={handleAddColumn} /> */}
          </div>
          <div className="jk-tr">
            {/* <button className={`jk-tb-btn${showFilt ? " active" : ""}`} onClick={() => setShowFilt(p => !p)}>
              <FiFilter size={13} /> Filter{hasFilter && <span className="jk-pip" />}
            </button>
            <span className="jk-total">{allTasks.length} tasks</span> */}
          </div>
        </div>

        {/* Filter bar */}
        {/* {showFilt && (
          <div className="jk-fbar">
            <div className="jk-fgrp">
              <label><FiUser size={11} /> Assignee</label>
              <select value={fAss} onChange={e => setFAss(e.target.value)}>
                <option value="all">All</option>
                {projectUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
            <div className="jk-fgrp">
              <label><FiFlag size={11} /> Priority</label>
              <select value={fPri} onChange={e => setFPri(e.target.value)}>
                <option value="all">All</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            {hasFilter && <button className="jk-clear" onClick={() => { setSearch(""); setFAss("all"); setFPri("all"); }}>
              <FiX size={11} /> Clear ({totalShown} shown)
            </button>}
          </div>
        )} */}

        {loading && !updating && <Loader />}

        {updating && (
          <>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
              backgroundColor: "rgba(255, 255, 255, 0.4)", backdropFilter: "blur(3px)", borderRadius: "8px"
            }} />
            <div style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 51,
              display: "flex", flexDirection: "column", alignItems: "center"
            }}>
              <div className="jk-spinner" />
              <span style={{ marginTop: "10px", fontWeight: "600", color: "#555" }}>
                Updating status...
              </span>
            </div>
          </>
        )}

        {(!loading || updating) && (
          <div className="jk-bwrap" style={{ position: "relative", minHeight: "300px" }}>
            <DndContext sensors={sensors} collisionDetection={closestCorners}
              onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
              <div className="jk-board">
                {columns.map(col => (
                  <Column key={col.id} col={col} tasks={visibleTasks(col.id)}
                    numericId={numericId} onOpen={openTask} onDelete={handleDeleteTask} onRefresh={onRefresh}
                    collapsedMap={collapsed} onToggle={id => setCollapsed(p => ({ ...p, [id]: !p[id] }))}
                    onUpdateCol={handleUpdateCol} onDeleteCol={handleDeleteCol} isClient={isClient} />
                ))}
              </div>
              <DragOverlay dropAnimation={{ duration: 200 }}>
                {activeTask ? <GhostCard task={activeTask} /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirm.open}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the task "${confirm.task?.title}"?`}
        onConfirm={executeDelete}
        onCancel={() => setConfirm({ open: false, task: null })}
      />
    </>
  );
};


export default SubtaskBoard;